/**
 * Utilities specific to handle the breakdown charts (chartjs)
 */
import { Breakdown, DataSet } from 'types'
import { GenericObject } from 'types/generalTypes'

import { COLOR } from 'designSystem/foundations/constants/color'

/**
 * Builds the portfolio allocation breakdown chart data
 * Extracts all the labels for each breakdown
 * Builds data arrays with respect to the common labels
 */
export function genPortfolioBreakdownChartData(
  current?: Breakdown,
  optimized?: Breakdown,
  benchmark?: Breakdown
) {
  // May need to be refactored to support an array (eg benchmark)
  const labels = getBreakdownLabels(current, optimized)

  const currentSeries = current && genDataSeries(current, labels)
  const optimizedSeries = optimized && genDataSeries(optimized, labels)
  const benchmarkSeries = benchmark && genDataSeries(benchmark, labels)

  const datasets = []

  if (currentSeries) {
    datasets.push({
      label: 'Current',
      data: currentSeries,
      backgroundColor: COLOR.grey500,
    })
  }

  if (optimizedSeries) {
    datasets.push({
      label: 'Optimized',
      data: optimizedSeries,
      backgroundColor: COLOR.purple400,
    })
  }

  if (benchmarkSeries) {
    datasets.push({
      label: 'Benchmark',
      data: benchmarkSeries,
      backgroundColor: COLOR.blue400,
    })
  }

  return {
    labels: labels.map((l) => l.split(' ')),
    datasets: datasets,
  }
}

/**
 * Gets all the labels from all breakdowns
 */
function getBreakdownLabels(current?: Breakdown, optimized?: Breakdown) {
  const labels: string[] = []

  if (current) labels.push(...current.labels)
  if (optimized) labels.push(...optimized.labels)

  const labelSet = new Set(labels)
  return Array.from(labelSet)
}

/**
 * Turns a dataset object into a label => value map
 */
export function genDataSetMap<T extends DataSet>(dataSet: T) {
  const { labels, values } = dataSet
  const map: GenericObject = {}

  for (let i = 0; i < labels.length; i++) {
    map[labels[i]] = values[i]
  }

  return map
}

/**
 * Builds a data series array with respect to the given labels
 * Coerces all values to numbers
 */
function genDataSeries<T extends DataSet>(
  dataSet: T,
  labels: string[]
): number[] {
  const dataMap = genDataSetMap(dataSet)
  const seriesData = new Array(labels.length).fill(0)

  for (let i = 0; i < labels.length; i++) {
    const label = labels[i]

    seriesData[i] = (Number(dataMap[label]) ?? 0) * 100
  }

  return seriesData
}
